import React from "react";
import {
  Avatar,
  Badge,
  Box,
  ListItemButton,
  Typography,
} from "@mui/material";
import LockRoundedIcon from "@mui/icons-material/LockRounded";

export const CHAT_BLUE = "#0057A4";
export const CHAT_BG = "#f5f7fa";
export const CHAT_MUTED = "#5f6b7a";
export const CHAT_LINE = "#e6eaf0";
export const CHAT_SOFT = "#e8f1fa";

export function classifyTeamChatChannels(channels) {
  const list = Array.isArray(channels) ? channels : [];
  const slug = (c) => String(c.slug || "").toLowerCase();
  const bySlug = (s) => list.find((c) => slug(c) === s) || null;
  const orgOrder = ["general", "sales", "support"];
  const orgChannels = orgOrder.map(bySlug).filter(Boolean);
  const orgIds = new Set(orgChannels.map((c) => c.id));
  const territoryChannels = list.filter((c) => !orgIds.has(c.id));
  return {
    orgChannels,
    territoryChannels,
    supportChannel: bySlug("support"),
  };
}

function numericId(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function channelLastMessageId(channel) {
  return numericId(channel?.last_message_id) || 0;
}

export function lastReadForChannel(lastReadByChannel, channelId) {
  if (!lastReadByChannel || channelId == null) return 0;
  const direct = numericId(lastReadByChannel[channelId]);
  if (direct != null) return direct;
  const asString = numericId(lastReadByChannel[String(channelId)]);
  return asString != null ? asString : 0;
}

export function channelIsUnread(channel, lastReadByChannel, selectedId) {
  if (!channel?.id) return false;
  if (numericId(channel.id) != null && numericId(channel.id) === numericId(selectedId)) {
    return false;
  }
  const lastId = channelLastMessageId(channel);
  if (!lastId) return false;
  return lastId > lastReadForChannel(lastReadByChannel, channel.id);
}

export function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        px: 1.25,
        mb: 0.7,
        mt: 0.5,
        fontSize: 10.5,
        color: "text.secondary",
        fontWeight: 800,
        letterSpacing: ".08em",
      }}
    >
      {children}
    </Typography>
  );
}

export function NavRow({ icon, label, selected, unread, locked, onClick, size = "default" }) {
  const comfortable = size === "comfortable";
  return (
    <ListItemButton
      onClick={onClick}
      selected={selected}
      sx={{
        minHeight: comfortable ? 48 : 38,
        mb: comfortable ? 0.55 : 0.4,
        px: 1.25,
        py: comfortable ? 1 : 0.6,
        borderRadius: 1.5,
        color: selected ? "text.primary" : "text.secondary",
        "&.Mui-selected": {
          bgcolor: "action.selected",
          color: "text.primary",
        },
        "&.Mui-selected:hover": { bgcolor: "action.selected" },
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mr: 1.1,
          color: selected ? "primary.main" : "text.secondary",
          "& svg": { fontSize: comfortable ? 22 : 18 },
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ flex: 1, fontSize: comfortable ? 16.5 : 14, fontWeight: selected ? 700 : 500 }}>
        {label}
      </Typography>
      {locked ? (
        <LockRoundedIcon sx={{ fontSize: 13, mr: unread ? 0.75 : 0, color: "text.secondary" }} />
      ) : null}
      {unread ? (
        <Box
          sx={{
            minWidth: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: "error.main",
            boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}`,
            flexShrink: 0,
          }}
          aria-label="Unread messages"
        />
      ) : null}
    </ListItemButton>
  );
}

export function PersonRow({ member, initials, online, onClick, variant = "nav" }) {
  const inbox = variant === "inbox";
  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        alignItems: "flex-start",
        px: inbox ? 2 : 1.25,
        py: inbox ? 1.7 : 0.7,
        borderRadius: inbox ? 0 : 1.5,
        mb: inbox ? 0 : 0.25,
        gap: 1.25,
        borderBottom: inbox ? "1px solid" : "none",
        borderColor: "divider",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Badge
        overlap="circular"
        variant="dot"
        invisible={!online}
        color="success"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Avatar
          sx={{
            width: inbox ? 38 : 34,
            height: inbox ? 38 : 34,
            bgcolor: "primary.main",
            fontSize: inbox ? 12 : 12,
            fontWeight: 800,
            mr: inbox ? 0 : 1.2,
          }}
        >
          {initials}
        </Avatar>
      </Badge>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography fontSize={inbox ? 13.2 : 13} fontWeight={inbox ? 800 : 700} noWrap>
          {member.name}
        </Typography>
        <Typography fontSize={inbox ? 11.5 : 11} color="text.secondary" noWrap sx={{ mt: inbox ? 0.35 : 0 }}>
          {member.role || member.username || ""}
        </Typography>
      </Box>
    </ListItemButton>
  );
}
